import { useMemo } from 'react'
import { CalendarClock, ShieldCheck, Sparkles, Plug, Link2 } from 'lucide-react'
import { connectedIssueTracker, isCodingAgentConnected } from '../lib/agentConnection'
import { useGetMonitoringStatusQuery } from '../services/sessionMonitoringApi'
import { isSelfHealingSetUp } from '../schemas/sessionMonitoring'
import { useGetProjectQuery, useGetSecurityOwnershipQuery, useGetTestingSchedulesQuery } from '../services/projectDataApi'
import { testingScheduleSummary } from '../lib/testingScheduleSummary'
import ProjectSetupBanner from './ProjectSetupBanner'
import SupportCard, { type SupportStatus } from './SupportCard'

const loading: SupportStatus = { tone: 'yellow', text: 'Checking status…' }
const unavailable: SupportStatus = { tone: 'yellow', text: 'Status unavailable — open to retry' }
const setup: SupportStatus = { tone: 'red', text: 'Not configured', setup: true }
const disabled: SupportStatus = { tone: 'gray', text: 'Disabled' }

export default function TestingSetupActions({ projectId, bannerOnly = false }: { projectId: string; bannerOnly?: boolean }) {
  const monitoring = useGetMonitoringStatusQuery(projectId, { pollingInterval: 30000, skipPollingIfUnfocused: true })
  const schedules = useGetTestingSchedulesQuery(projectId, { pollingInterval: 60000, skipPollingIfUnfocused: true })
  const projectQuery = useGetProjectQuery(projectId, { pollingInterval: 15000, skipPollingIfUnfocused: true })
  const project = projectQuery.currentData
  const securityEnabled = project?.enabled_polish_passes?.includes('security') === true
  const exempt = project?.security_ownership_exempt === true
  const ownership = useGetSecurityOwnershipQuery({ projectId, targetUrl: project?.target_url ?? '', nonce: project?.security_nonce ?? '' }, {
    skip: !project?.security_nonce || exempt || !securityEnabled,
    pollingInterval: 15000, skipPollingIfUnfocused: true,
  })
  const status = monitoring.currentData
  const savedSchedules = schedules.currentData?.schedules
  const currentMinute = Math.floor(Date.now() / 60000)
  const scheduleSummary = useMemo(() => bannerOnly || !savedSchedules ? ''
    : testingScheduleSummary(savedSchedules, new Date(currentMinute * 60000)), [savedSchedules, currentMinute, bannerOnly])
  if (bannerOnly) {
    const setupComplete = Boolean(project && status?.has_sessions && schedules.currentData?.schedules.some(schedule => schedule.config.enabled)
      && (!securityEnabled || exempt || ownership.currentData?.latest?.verified))
    return status?.can_edit ? <ProjectSetupBanner key={projectId} projectId={projectId} complete={setupComplete} /> : null
  }
  const healing: SupportStatus = monitoring.isError ? unavailable : !status ? loading
    : !isSelfHealingSetUp(status) ? { tone: 'red', text: 'Watch user sessions for bugs', setup: true } : !status.enabled ? disabled
    : !status.has_sessions ? { tone: 'yellow', text: 'Waiting for the first session' }
    : { tone: 'white', text: `${status.sessions_last_24h.toLocaleString()} sessions · ${status.bugs_last_24h.toLocaleString()} bugs in the last 24 hours` }
  const testing: SupportStatus = schedules.isError ? unavailable : !savedSchedules ? loading
    : savedSchedules.length === 0 ? setup : !savedSchedules.some(schedule => schedule.config.enabled) ? disabled
    : { tone: 'white', text: scheduleSummary }
  const ownershipRequired: SupportStatus = { tone: 'red', text: 'Confirm ownership to view security findings' }
  const activeSecurity: SupportStatus = monitoring.isError ? unavailable : !status ? loading
    : { tone: 'white', text: `${status.open_security_bugs.toLocaleString()} open security bugs` }
  const security: SupportStatus = projectQuery.isError ? unavailable : !project ? loading
    : !securityEnabled ? disabled
    : !exempt && !project.security_nonce ? ownershipRequired
    : exempt ? activeSecurity
    : ownership.isError ? unavailable : !ownership.currentData ? loading
    : !ownership.currentData.latest?.verified ? ownershipRequired
    : activeSecurity
  const tracker = project && connectedIssueTracker(project)
  const agent: SupportStatus = projectQuery.isError ? unavailable : !project ? loading
    : isCodingAgentConnected(project) ? { tone: 'white', text: `Connected, ${project.agent_read_bug_count ?? 0} bugs read, ${project.resolved_bug_count ?? 0} resolved` }
    : tracker ? { tone: 'gray', text: `Not connected, bugs go to ${tracker.name}` }
    : { tone: 'red', text: 'Agent not connected' }
  const issueTracker: SupportStatus = projectQuery.isError ? unavailable : !project ? loading
    : tracker ? { tone: 'white', text: `${tracker.name} connected, ${project.issue_tracker_filed_bug_count ?? 0} bugs filed, ${project.resolved_bug_count ?? 0} resolved` }
    : { tone: 'gray', text: 'Send new bugs to GitHub Issues, GitLab, Linear, Jira, or Asana' }
  return <div className="testing-setup-actions" aria-label="Project support">
    <SupportCard title={healing.tone === 'white' ? 'Self Healing Active' : 'Self Healing'} icon={Sparkles} to={`/projects/${projectId}/support/self-healing`} status={healing} />
    <SupportCard title="Test Schedule" icon={CalendarClock} to={`/projects/${projectId}/support/test-schedule`} status={testing} />
    <SupportCard title="Security" icon={ShieldCheck} to={`/projects/${projectId}/security`} status={security} />
    <SupportCard title="Agent Connection" icon={Plug} to={`/projects/${projectId}/support/agent-connection`} status={agent} />
    <SupportCard title={tracker ? 'Issue Tracker' : 'Connect Issue Tracker'} icon={Link2} to={`/projects/${projectId}/support/issue-tracker`} status={issueTracker} />
  </div>
}
